import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import centralAxiosInstance from '../centralAxiosInstance';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';

/**
 * Central registry: GET /api/v1/tenants (SPA route /tenants stays on the React app).
 */
const useTenantsService = () => {
  const [tenants, setTenants] = useState([]);
  const [isLoading, toggleLoading] = useToggle();

  const fetchTenants = useCallback(async () => {
    toggleLoading();
    try {
      const response = await centralAxiosInstance.get('/tenants');
      const data = response.data;
      setTenants(Array.isArray(data) ? data : []);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to load tenants.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load tenants.');
      console.error('Fetch tenants error:', error);
      setTenants([]);
    } finally {
      toggleLoading();
    }
  }, [toggleLoading]);

  const createTenant = useCallback(async (payload) => {
    const response = await centralAxiosInstance.post('/tenants', payload);
    return response.data;
  }, []);

  const getTenantById = useCallback(async (id) => {
    const response = await centralAxiosInstance.get(`/tenants/${id}`);
    return response.data;
  }, []);

  const updateTenant = useCallback(async (id, payload) => {
    const response = await centralAxiosInstance.put(`/tenants/${id}`, payload);
    return response.data;
  }, []);

  /** Backend has no DELETE; marks tenant as removed via PATCH /api/v1/tenants/{id}/status */
  const setTenantStatus = useCallback(async (id, status) => {
    const response = await centralAxiosInstance.patch(
      `/tenants/${id}/status`,
      { status }
    );
    return response.data;
  }, []);

  const getTenantSettings = useCallback(async (id) => {
    const response = await axiosInstance.get('/tenant-settings', {
      params: { tenantId: id },
    });
    return response.data?.data ?? response.data;
  }, []);

  const updateTenantSettings = useCallback(async (id, payload) => {
    const response = await axiosInstance.patch('/tenant-settings', payload, {
      params: { tenantId: id },
    });
    return response.data?.data ?? response.data;
  }, []);

  return {
    tenants,
    fetchTenants,
    isLoading,
    createTenant,
    getTenantById,
    updateTenant,
    setTenantStatus,
    getTenantSettings,
    updateTenantSettings,
  };
};

export default useTenantsService;
